<template>
    <div>
        <div
            :class="[
                'cz2__input-box',
                'cz2__input-box--active',
                {
                    'cz2__input-box--small-font': smallFont,
                    'cz2__input-box--invalid': invalid,
                    'cz2__input-box--outline': outlined,
                },
            ]">
            <input
                ref="input"
                type="text"
                :maxlength="lengthLimit"
                :value="inputText"
                :placeholder="inputPlaceholder"
                @focus="focused"
                @blur="closeKeyboard"
                @input="emitInput"
                :aria-labelledby="labelledBy"
                :readonly="readOnly"
            >

            <span class="cz2__input-box__error">
                {{ errorText }}
            </span>
        </div>
    </div>
</template>

<style lang="scss">
    @import "../../style/variables.scss";

    #cz2.cz2 {
        .cz2__input-box {
            position: relative;

            height: 48px;
            border-radius: 8px;

            input {
                width: 100%;
                height: 100%;

                background: #fff;

                border: 1px solid #111;
                border-radius: 8px;

                padding: 0 24px;

                font-size: 19px;
                text-transform: uppercase;
                font-family: var(--cz-font-700-family);
                font-weight: 700;
            }

            &.cz2__input-box--active {
                box-shadow: 9px 9px 0px #111;
            }

            &.cz2__input-box--small-font {
                span {
                    font-size: 16px;
                }
            }

            &.cz2__input-box--outline {
                // Override no-outlines.
                outline: 2px solid #fff !important;
                outline-offset: 2px;
            }

            .cz2__input-box__error {
                display: none;
            }

            &.cz2__input-box--invalid {
                input {
                    border-color: #de2839;

                    background: #fef0f0;
                }

                &.cz2__input-box--active {
                    box-shadow: 6px 6px 0px #de2839;

                    @include respond-above(sm) {
                        box-shadow: 9px 9px 0px #de2839;
                    }
                }

                .cz2__input-box__error {
                    display: block;
                    position: absolute;

                    left: 0;
                    top: 65px;

                    margin-top: -7px;
                    @include font(bodyRegular);
                    font-size: 12px;
                    line-height: 1.2;
                    color: color(primaryWhite);

                    &:before {
                        content: '';

                        display: inline-block;
                        vertical-align: -3px;

                        width: 16px;
                        height: 16px;

                        background: transparent url(../../assets/images/warning@2x.png) no-repeat center/contain;

                        margin-right: 5px;
                    }

                    @include respond-above(sm) {
                        margin-top: 0;
                        font-size: 13px;
                    }

                    @include respond-above(lg) {
                        @include font(bodyRegular);
                    }
                }
            }

            .cz2__input-box__label {
                position: absolute;

                left: 24px;
                top: 20px;

                font-family: var(--cz-font-400-family);
                font-weight: var(--cz-font-400-weight);
                font-style: normal;
                font-size: 18px;
                line-height: 18px;
                letter-spacing: -0.3px;

                color: #4c4c4c;

                transition: all $transition-fast ease-out;
            }

            &:not(.cz2__input-box--empty):not(.cz2__input-box--active) {
                .cz2__input-box__label {
                    left: 0;
                    top: -30px;

                    font-size: 11px;
                    line-height: 11px;

                    color: #fff;
                }
            }

            &.cz2__input-box--active {
                .cz2__input-box__label {
                    left: 0;
                    top: -30px;

                    font-size: 11px;
                    line-height: 11px;

                    color: #fff;
                }

                &.cz2__input-box--empty {
                    .cz2__input-box__label {
                        left: 24px;
                        top: 20px;

                        font-size: 18px;
                        line-height: 18px;

                        color: #4c4c4c;
                    }
                }
            }

            span {
                position: absolute;

                left: 24px;
                right: 24px;

                top: 0;
                height: 100%;

                overflow: hidden;

                @include font(h1);
                text-transform: none;
                line-height: 64px;

                color: #111;
            }
        }
    }

    #cz2.cz2.cz2__is-sb {
        .cz2__input-box.cz2__input-box--invalid {
            .cz2__input-box__error {
                color: #000;
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__input-box {
            height: 68px;

            input {
                font-size: 28px;

                font-family: var(--cz-font-900-family);
                font-weight: var(--cz-font-900-weight);
            }
        }

        .cz2__input-box.cz2__input-box--invalid {
            .cz2__input-box__error {
                margin-top: 22px;

                font-size: 28px;

                font-family: var(--cz-font-900-family);
                font-weight: var(--cz-font-900-weight);

                &:before {
                    width: 24px;
                    height: 24px;
                }
            }
        }
    }
</style>

<script>
    export default {
        props: {
            inputText: {
                type: String,
                default: '',
            },
            inputPlaceholder: {
                type: String,
                default: '',
            },
            invalid: {
                type: Boolean,
                default: false,
            },
            errorText: {
                type: String,
                default: '',
            },
            maximumLength: {
                type: Number,
                default: null,
            },
            smallFont: {
                type: Boolean,
                default: false,
            },
            labelledBy: {
                type: String,
                default: '',
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            outlined: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            lengthLimit() {
                return this.maximumLength + 1;
            },

            isMobile() {
                return this.$store.state.customizer.isMobile;
            },
        },

        methods: {
            emitInput() {
                this.$emit('input', this.$refs.input.value);
            },

            focused() {
                this.$emit('focused');

                this.openKeyboard();
            },

            openKeyboard() {
                if (this.isMobile) {
                    this.$store.dispatch('setIsKeyboardOpen', true);
                }
            },

            closeKeyboard() {
                if (this.isMobile) {
                    this.$store.dispatch('setIsKeyboardOpen', false);
                }
            },
        },
    };
</script>
